import { z } from "zod";

import { platformQueryKeys } from "../platform-query-key-factory";

import type { ERROR_CODE_I18N_MAPPING } from "../config/api-config/error-i18n-mappings";
import type { QueryKey } from "@tanstack/react-query";
import type { AxiosError } from "axios";

export type ProductCode = "hr" | "ats";

export type HrUserUpdateModel = {
  userId: string;
  email: string;
  displayName?: string;
  securityGroups: string[];
};

export type HrUserBulkUpdateModel = {
  users: string[];
  securityGroups: string[];
};

export type AdminSettingsUserBase = {
  userId: string;
  name: string;
  initials?: string;
  avatarUri?: string;
  avatarColor?: string;
  securityGroups: SecurityGroup[];
  email: string;
};

export type PlatformUser = {
  id: string;
  displayName: string;
  email: string;
};

export type CreatePlatformUserModel = {
  email?: string;
  displayName?: string;
  securityGroups: string[];
};

export type HrAdminSettingsUser = AdminSettingsUserBase & {
  employeeNumber: string;
  department: string;
  jobTitle: string;
};

// Outcome of emailing a user when an administrator added them; missing when no email was attempted
export type EmailStatus = "SENT" | "FAILED";

export type AtsAdminSettingsUser = AdminSettingsUserBase & {
  emailStatus?: EmailStatus;
  atsUserReady?: boolean;
  /** Client-only marker for a newly created user awaiting ATS provisioning. */
  atsUserProvisioningPending?: boolean;
};

export interface SecurityGroup {
  id: string;
  name: string;
}

export interface SecurityGroupSummary {
  id: string;
  name: string;
  userCount: number;
  roleCodes: string[];
}
export interface SelectOption {
  label: string;
  value: string;
}

export interface ApiValidationError {
  field: string;
  code: ApiErrorCode;
  message: string;
}

export const securityGroupsQueryKeyMap: Record<ProductCode, QueryKey> = {
  ats: platformQueryKeys.atsSecurityGroups(),
  hr: platformQueryKeys.hrSecurityGroups(),
};

export const usersQueryKeyMap: Record<ProductCode, QueryKey> = {
  ats: platformQueryKeys.atsUsers(),
  hr: platformQueryKeys.hrUsers(),
};

export const headerCell = (testId: string) => () =>
  ({ "data-testid": testId }) as React.HTMLAttributes<HTMLElement>;

export const ValidationErrorSchema = z.object({
  field: z.string(),
  code: z.string() as z.ZodType<ApiErrorCode>,
  message: z.string(),
  params: z
    .record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.array(z.string())]))
    .optional(),
});

export const ApiErrorResponseSchema = z.object({
  correlationId: z.string(),
  error: z.object({ code: z.string(), message: z.string() }),
  status: z.string(),
  statusCode: z.number(),
  timestamp: z.string(),
  validationErrors: z.array(ValidationErrorSchema),
});

export type ApiErrorResponse = z.infer<typeof ApiErrorResponseSchema>;
export type ValidationError = z.infer<typeof ValidationErrorSchema>;

export type ApiError = AxiosError<ApiErrorResponse>;
export type PlatformApiError = ApiError;

export type ApiErrorCode = keyof typeof ERROR_CODE_I18N_MAPPING;

export interface ApiValidationError {
  field: string;
  code: ApiErrorCode;
  message: string;
}
